import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiArrowLeft, FiPlus, FiCheck, FiX, FiEdit2, FiTrash2, FiBriefcase, FiUsers, FiDollarSign, FiSave, FiTrendingUp, FiCheckCircle, FiTag, FiCalendar, FiLoader, FiClock, FiShield, FiPercent, FiInfo, FiLayers } = FiIcons;

const PricingManagement = () => {
  const navigate = useNavigate();
  const {
    subscriptionPlans = [],
    addSubscriptionPlan,
    updateSubscriptionPlan,
    deleteSubscriptionPlan,
    promoOffers = [],
    addPromoOffer,
    updatePromoOffer,
    deletePromoOffer,
    investigationPricing,
    updateInvestigationPricing,
    externalServices = [],
    addExternalService,
    updateExternalService,
    deleteExternalService
  } = useData();

  const [activeTab, setActiveTab] = useState('plans');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal states
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  const [showPromoModal, setShowPromoModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);

  const [showExternalServiceModal, setShowExternalServiceModal] = useState(false);
  const [editingExternalService, setEditingExternalService] = useState(null);

  // Form states
  const [planForm, setPlanForm] = useState({
    id: '', name: '', price: '', employees: '', features: '', popular: false, active: true
  });

  const [promoForm, setPromoForm] = useState({
    name: '', code: '', type: 'percentage', value: '', active: true,
    valid_from: '', valid_to: '', validity_type: 'fixed', duration_count: 1, duration_unit: 'months'
  });

  const [externalServiceForm, setExternalServiceForm] = useState({
    name: '', price: '', timeframe: 'mnd', description: '', category: 'external', active: true
  });

  const [prices, setPrices] = useState({
    company: { standard: 0, externalFrom: 0 },
    investigator: { basic: 0, pro: 0, partner: 0 },
    commission: { consultant: 0, advocate: 0, partnerMin: 0, partnerMax: 0 }
  });

  useEffect(() => {
    if (investigationPricing) {
      setPrices({
        company: investigationPricing.company || { standard: 0, externalFrom: 0 },
        investigator: investigationPricing.investigator || { basic: 0, pro: 0, partner: 0 },
        commission: investigationPricing.commission || { consultant: 0, advocate: 0, partnerMin: 0, partnerMax: 0 }
      });
    }
  }, [investigationPricing]);

  const handleSavePlan = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        name: planForm.name,
        price: Number(planForm.price),
        employees: planForm.employees,
        features: planForm.features.split('\n').filter(f => f.trim() !== ''),
        popular: planForm.popular,
        active: planForm.active
      };

      let result;
      if (editingPlan) {
        if (typeof updateSubscriptionPlan !== 'function') {
          throw new Error('Funksjonen updateSubscriptionPlan mangler i systemet.');
        }
        result = await updateSubscriptionPlan(editingPlan.id, payload);
      } else {
        if (typeof addSubscriptionPlan !== 'function') {
          throw new Error('Funksjonen addSubscriptionPlan mangler i systemet.');
        }
        const newId = payload.name.toLowerCase().replace(/\s+/g, '-');
        result = await addSubscriptionPlan({ ...payload, id: newId });
      }

      if (result && result.success === false) {
        throw new Error(result.error?.message || "Ukjent databasefeil");
      }

      setShowPlanModal(false);
      setEditingPlan(null);
    } catch (error) {
      console.error("Save error:", error);
      alert("Kunne ikke lagre: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSavePromo = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        name: promoForm.name,
        code: promoForm.code.toUpperCase(),
        type: promoForm.type,
        value: Number(promoForm.value),
        active: promoForm.active,
        validity_type: promoForm.validity_type,
        valid_from: promoForm.validity_type === 'fixed' ? (promoForm.valid_from || null) : null,
        valid_to: promoForm.validity_type === 'fixed' ? (promoForm.valid_to || null) : null,
        duration_count: promoForm.validity_type === 'relative' ? Number(promoForm.duration_count) : null,
        duration_unit: promoForm.validity_type === 'relative' ? promoForm.duration_unit : null
      };

      let result;
      if (editingPromo) {
        if (typeof updatePromoOffer !== 'function') {
          throw new Error('Funksjonen updatePromoOffer mangler i systemet.');
        }
        result = await updatePromoOffer(editingPromo.id, payload);
      } else {
        if (typeof addPromoOffer !== 'function') {
          throw new Error('Funksjonen addPromoOffer mangler i systemet.');
        }
        result = await addPromoOffer(payload);
      }

      if (result && result.success === false) {
        throw new Error(result.error?.message || "Ukjent databasefeil");
      }

      setShowPromoModal(false);
      setEditingPromo(null);
    } catch (error) {
      console.error("Save error:", error);
      alert("Kunne ikke lagre: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveExternalService = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        name: externalServiceForm.name,
        price: Number(externalServiceForm.price),
        timeframe: externalServiceForm.timeframe,
        description: externalServiceForm.description,
        category: externalServiceForm.category,
        active: externalServiceForm.active,
        icon: 'FiShield'
      };

      let result;
      if (editingExternalService) {
        if (typeof updateExternalService !== 'function') throw new Error('Oppdateringsfunksjon mangler i systemet.');
        result = await updateExternalService(editingExternalService.id, payload);
      } else {
        if (typeof addExternalService !== 'function') throw new Error('Lagringsfunksjon mangler i systemet.');
        result = await addExternalService(payload);
      }

      if (result && result.success === false) throw new Error(result.error?.message || "Ukjent feil ved lagring i database");

      setShowExternalServiceModal(false);
      setEditingExternalService(null);
    } catch (error) {
      console.error("Save error:", error);
      alert("Kunne ikke lagre: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      id: plan.id,
      name: plan.name,
      price: plan.price,
      employees: plan.employees,
      features: Array.isArray(plan.features) ? plan.features.join('\n') : '',
      popular: plan.popular || false,
      active: plan.active ?? true
    });
    setShowPlanModal(true);
  };

  const handleEditPromo = (promo) => {
    setEditingPromo(promo);
    setPromoForm({
      name: promo.name,
      code: promo.code,
      type: promo.type,
      value: promo.value,
      active: promo.active ?? true,
      valid_from: promo.valid_from || '',
      valid_to: promo.valid_to || '',
      validity_type: promo.validity_type || 'fixed',
      duration_count: promo.duration_count || 1,
      duration_unit: promo.duration_unit || 'months'
    });
    setShowPromoModal(true);
  };

  const handleEditExternalService = (service) => {
    setEditingExternalService(service);
    setExternalServiceForm({
      name: service.name,
      price: service.price,
      timeframe: service.timeframe || 'mnd',
      description: service.description || '',
      category: service.category || 'external',
      active: service.active ?? true
    });
    setShowExternalServiceModal(true);
  };

  const handleDeletePlan = async (id) => {
    if (!window.confirm("Er du sikker på at du vil slette denne pakken?")) return;
    try {
      if (typeof deleteSubscriptionPlan !== 'function') throw new Error('Slettefunksjon mangler i systemet.');
      await deleteSubscriptionPlan(id);
    } catch (error) {
      alert("Kunne ikke slette: " + error.message);
    }
  };

  const handleDeletePromo = async (id) => {
    if (!window.confirm("Er du sikker på at du vil slette denne kampanjen?")) return;
    try {
      if (typeof deletePromoOffer !== 'function') throw new Error('Slettefunksjon mangler i systemet.');
      await deletePromoOffer(id);
    } catch (error) {
      alert("Kunne ikke slette: " + error.message);
    }
  };

  const handleDeleteExternalService = async (id) => {
    if (!window.confirm("Er du sikker på at du vil slette denne tjenesten?")) return;
    try {
      if (typeof deleteExternalService !== 'function') throw new Error('Slettefunksjon for tjenester mangler i systemet.');
      await deleteExternalService(id);
    } catch (error) {
      alert("Kunne ikke slette: " + error.message);
    }
  };

  const handleSavePartnerPricing = async () => {
    setIsSubmitting(true);
    try {
      if (typeof updateInvestigationPricing !== 'function') {
        throw new Error('Funksjonen updateInvestigationPricing mangler i systemet.');
      }
      await updateInvestigationPricing(prices);
      alert("Lagret!");
    } catch (error) {
      console.error("Save error:", error);
      alert("Kunne ikke lagre: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <header className="bg-white border-b p-6 flex justify-between items-center sticky top-0 z-20 shadow-sm">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/superadmin')} className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors">
            <SafeIcon icon={FiArrowLeft} />
          </button>
          <div className="flex flex-col">
            <h1 className="text-xl font-bold text-gray-900">Priser og Rabatter</h1>
            <p className="text-sm text-gray-500">Administrer abonnement, kampanjer og partnerpriser</p>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto p-8">
        <div className="flex gap-8 border-b border-gray-200 mb-10 overflow-x-auto whitespace-nowrap hide-scrollbar">
          <button onClick={() => setActiveTab('plans')} className={`pb-4 px-2 text-sm font-bold transition-all ${activeTab === 'plans' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            Abonnementsplaner
          </button>
          <button onClick={() => setActiveTab('promos')} className={`pb-4 px-2 text-sm font-bold transition-all ${activeTab === 'promos' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            Kampanjer og Rabatter
          </button>
          <button onClick={() => setActiveTab('external')} className={`pb-4 px-2 text-sm font-bold transition-all flex items-center gap-2 ${activeTab === 'external' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            <SafeIcon icon={FiLayers} /> Tilleggstjenester
          </button>
          <button onClick={() => setActiveTab('partner')} className={`pb-4 px-2 text-sm font-bold transition-all flex items-center gap-2 ${activeTab === 'partner' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            <SafeIcon icon={FiBriefcase} /> Utredning & Partner
          </button>
        </div>

        {activeTab === 'plans' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Aktive planer (Bedrift)</h2>
              <button onClick={() => {
                setEditingPlan(null);
                setPlanForm({ name: '', price: '', employees: '', features: '', popular: false, active: true });
                setShowPlanModal(true);
              }} className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-bold hover:bg-blue-700 shadow-sm">
                <SafeIcon icon={FiPlus} /> Ny plan
              </button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(subscriptionPlans || []).map(plan => (
                <motion.div key={plan.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all relative flex flex-col">
                  <div className="absolute top-4 right-4 flex gap-2">
                    <button onClick={() => handleEditPlan(plan)} className="text-gray-400 hover:text-blue-600 p-1.5 hover:bg-blue-50 rounded-lg transition-all"><SafeIcon icon={FiEdit2} /></button>
                    <button onClick={() => handleDeletePlan(plan.id)} className="text-gray-400 hover:text-red-600 p-1.5 hover:bg-red-50 rounded-lg transition-all"><SafeIcon icon={FiTrash2} /></button>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{plan.name}</h3>
                  <div className="text-2xl font-black text-blue-600 mb-1">
                    {plan.price} kr <span className="text-sm font-normal text-gray-500">/mnd</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-6 font-medium">
                    <SafeIcon icon={FiUsers} /> {plan.employees}
                  </div>
                  <ul className="space-y-3 mb-6 flex-1">
                    {(plan.features || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <SafeIcon icon={FiCheck} className="text-green-500 mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-2">
                    {plan.active && <span className="bg-green-100 text-green-800 text-[10px] font-black uppercase px-2 py-1 rounded-full">Aktiv</span>}
                    {plan.popular && <span className="bg-blue-100 text-blue-800 text-[10px] font-black uppercase px-2 py-1 rounded-full">Populær</span>}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'promos' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Aktive Kampanjer</h2>
              <button onClick={() => {
                setEditingPromo(null);
                setPromoForm({ name: '', code: '', type: 'percentage', value: '', active: true, valid_from: '', valid_to: '', validity_type: 'fixed', duration_count: 1, duration_unit: 'months' });
                setShowPromoModal(true);
              }} className="bg-[#ea580c] text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-bold hover:bg-[#c2410c] shadow-md">
                <SafeIcon icon={FiPlus} /> Ny kampanje
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {(promoOffers || []).map(promo => (
                <div key={promo.id} className="bg-white border border-gray-200 rounded-2xl p-6 relative shadow-sm">
                  <div className="absolute top-4 right-4 flex gap-2">
                    <button onClick={() => handleEditPromo(promo)} className="text-gray-400 hover:text-blue-600 p-1.5 rounded-lg transition-colors"><SafeIcon icon={FiEdit2} /></button>
                    <button onClick={() => handleDeletePromo(promo.id)} className="text-gray-400 hover:text-red-600 p-1.5 rounded-lg transition-colors"><SafeIcon icon={FiTrash2} /></button>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold text-gray-900">{promo.name}</h3>
                    <span className="bg-gray-100 text-gray-700 text-xs font-mono px-2.5 py-1 rounded-lg border border-gray-200 uppercase font-bold">{promo.code}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[#ea580c] font-black text-lg">
                    <SafeIcon icon={promo.type === 'percentage' ? FiTrendingUp : FiDollarSign} />
                    {promo.type === 'percentage' ? `${promo.value}% rabatt` : `${promo.value} kr rabatt`}
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-50 flex justify-between items-center">
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <SafeIcon icon={promo.validity_type === 'relative' ? FiClock : FiCalendar} />
                      {promo.validity_type === 'relative' ? (
                        <span>Varighet: {promo.duration_count} {promo.duration_unit === 'days' ? 'dager' : promo.duration_unit === 'months' ? 'måneder' : 'år'}</span>
                      ) : (
                        <span>Fast periode</span>
                      )}
                    </div>
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${promo.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {promo.active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TILLEGGSTJENESTER FANE (NY) */}
        {activeTab === 'external' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Aktive Tilleggstjenester</h2>
              <button onClick={() => {
                setEditingExternalService(null);
                setExternalServiceForm({ name: '', price: '', timeframe: 'mnd', description: '', category: 'external', active: true });
                setShowExternalServiceModal(true);
              }} className="bg-purple-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-bold hover:bg-purple-700 shadow-md">
                <SafeIcon icon={FiPlus} /> Ny tjeneste
              </button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(externalServices || []).map(service => (
                <div key={service.id} className="bg-white border border-gray-200 rounded-2xl p-6 relative shadow-sm hover:shadow-md transition-shadow">
                  <div className="absolute top-4 right-4 flex gap-2">
                    <button onClick={() => handleEditExternalService(service)} className="text-gray-400 hover:text-blue-600 p-1.5 hover:bg-blue-50 rounded-lg transition-colors"><SafeIcon icon={FiEdit2} /></button>
                    <button onClick={() => handleDeleteExternalService(service.id)} className="text-gray-400 hover:text-red-600 p-1.5 hover:bg-red-50 rounded-lg transition-colors"><SafeIcon icon={FiTrash2} /></button>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                      <SafeIcon icon={service.icon === 'FiBriefcase' ? FiBriefcase : FiShield} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 max-w-[70%] truncate">{service.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4 h-10 overflow-hidden line-clamp-2" title={service.description}>{service.description}</p>
                  <div className="flex items-center gap-2 text-purple-600 font-black text-lg">
                    {service.price} kr <span className="text-sm font-normal text-gray-500">/ {service.timeframe}</span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-50 flex justify-between items-center">
                    <span className="bg-gray-100 text-gray-700 text-xs font-mono px-2.5 py-1 rounded-lg border border-gray-200 uppercase font-bold">{service.category}</span>
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${service.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {service.active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {externalServices?.length === 0 && (
                <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-gray-300">
                    <SafeIcon icon={FiLayers} className="mx-auto text-4xl text-gray-300 mb-2" />
                    <h3 className="text-gray-900 font-bold">Ingen tilleggstjenester lagt til enda</h3>
                    <p className="text-gray-500 text-sm">Opprett tjenester for at de skal vises i registreringsskjemaet.</p>
                </div>
            )}
          </div>
        )}

        {activeTab === 'partner' && (
          <div className="space-y-8">
            <div className="flex justify-between items-center bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <h2 className="text-2xl font-black text-gray-900">Utredning og Partner Prismodell</h2>
              <button 
                onClick={handleSavePartnerPricing}
                disabled={isSubmitting}
                className={`text-white px-8 py-3 rounded-xl flex items-center gap-2 font-black shadow-lg transition-colors ${isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {isSubmitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSave} />}
                {isSubmitting ? 'Lagrer...' : 'Lagre endringer'}
              </button>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* KOLONNE 1: BEDRIFT */}
              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                <h3 className="font-bold mb-6 flex items-center gap-2 text-blue-600">
                  <SafeIcon icon={FiBriefcase} /> Bedrift-priser
                </h3>
                <div className="space-y-5">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Standard HMS Utredning (kr)</label>
                    <div className="relative">
                      <SafeIcon icon={FiDollarSign} className="absolute left-3 top-3.5 text-gray-400" />
                      <input type="number" value={prices.company.standard} onChange={e => setPrices({ ...prices, company: { ...prices.company, standard: parseInt(e.target.value) || 0 } })} className="w-full pl-10 p-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500" />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Ekstern Utreder Fra-pris (kr)</label>
                    <div className="relative">
                      <SafeIcon icon={FiTrendingUp} className="absolute left-3 top-3.5 text-gray-400" />
                      <input type="number" value={prices.company.externalFrom} onChange={e => setPrices({ ...prices, company: { ...prices.company, externalFrom: parseInt(e.target.value) || 0 } })} className="w-full pl-10 p-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500" />
                    </div>
                  </div>
                </div>
              </div>

              {/* KOLONNE 2: UTREDER ABONNEMENT */}
              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                <h3 className="font-bold mb-6 flex items-center gap-2 text-purple-600">
                  <SafeIcon icon={FiUsers} /> Utreder Abonnement
                </h3>
                <div className="space-y-5">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Basic Plan (kr/mnd)</label>
                    <input type="number" value={prices.investigator.basic} onChange={e => setPrices({ ...prices, investigator: { ...prices.investigator, basic: parseInt(e.target.value) || 0 } })} className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:border-purple-500" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Pro Plan (kr/mnd)</label>
                    <input type="number" value={prices.investigator.pro} onChange={e => setPrices({ ...prices, investigator: { ...prices.investigator, pro: parseInt(e.target.value) || 0 } })} className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:border-purple-500" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Partner Plan (kr/mnd)</label>
                    <input type="number" value={prices.investigator.partner} onChange={e => setPrices({ ...prices, investigator: { ...prices.investigator, partner: parseInt(e.target.value) || 0 } })} className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:border-purple-500" />
                  </div>
                </div>
              </div>

              {/* KOLONNE 3: PROVISJON */}
              <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                <h3 className="font-bold mb-6 flex items-center gap-2 text-green-600">
                  <SafeIcon icon={FiPercent} /> Provisjonsmodell
                </h3>
                <div className="space-y-5">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Konsulent / Revisor (%)</label>
                    <input type="number" value={prices.commission.consultant} onChange={e => setPrices({ ...prices, commission: { ...prices.commission, consultant: parseInt(e.target.value) || 0 } })} className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:border-green-500" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Advokat (%)</label>
                    <input type="number" value={prices.commission.advocate} onChange={e => setPrices({ ...prices, commission: { ...prices.commission, advocate: parseInt(e.target.value) || 0 } })} className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:border-green-500" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Partner Min (%)</label>
                      <input type="number" value={prices.commission.partnerMin} onChange={e => setPrices({ ...prices, commission: { ...prices.commission, partnerMin: parseInt(e.target.value) || 0 } })} className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:border-green-500" />
                    </div>
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Partner Max (%)</label>
                      <input type="number" value={prices.commission.partnerMax} onChange={e => setPrices({ ...prices, commission: { ...prices.commission, partnerMax: parseInt(e.target.value) || 0 } })} className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:border-green-500" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* INFO BOKS */}
            <div className="bg-blue-50 border border-blue-100 p-6 rounded-2xl flex items-start gap-4">
              <SafeIcon icon={FiInfo} className="text-blue-600 text-xl mt-1" />
              <div>
                <h4 className="font-bold text-blue-900 mb-1">Om prismodellen for utredning</h4>
                <p className="text-sm text-blue-800 leading-relaxed">
                  Disse verdiene brukes globalt i systemet for å beregne honorarer, provisjoner og abonnementspriser for utredere.
                  Endringer her vil påvirke nye beregninger umiddelbart. Provisjonssatser brukes til intern rapportering overfor partnere.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* MODAL: PLAN */}
        <AnimatePresence>
          {showPlanModal && (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden">
                <div className="p-6 border-b flex justify-between items-center bg-gray-50">
                  <h3 className="text-xl font-bold">{editingPlan ? 'Rediger Pakke' : 'Ny Pakke'}</h3>
                  <button onClick={() => setShowPlanModal(false)}><SafeIcon icon={FiX} size={24} /></button>
                </div>
                <form onSubmit={handleSavePlan} className="p-8 space-y-4">
                  <input type="text" placeholder="Navn på pakke" required value={planForm.name} onChange={e => setPlanForm({ ...planForm, name: e.target.value })} className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:border-blue-500" />
                  <div className="grid grid-cols-2 gap-4">
                    <input type="number" placeholder="Pris (NOK)" required value={planForm.price} onChange={e => setPlanForm({ ...planForm, price: e.target.value })} className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:border-blue-500" />
                    <input type="text" placeholder="Antall ansatte tekst" required value={planForm.employees} onChange={e => setPlanForm({ ...planForm, employees: e.target.value })} className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:border-blue-500" />
                  </div>
                  <textarea placeholder="Fordeler (en per linje)" rows={5} value={planForm.features} onChange={e => setPlanForm({ ...planForm, features: e.target.value })} className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:border-blue-500 resize-none" />
                  <div className="flex gap-4 p-2 bg-gray-50 rounded-xl border border-gray-100">
                    <label className="flex items-center gap-2 cursor-pointer text-sm font-medium"><input type="checkbox" checked={planForm.popular} onChange={e => setPlanForm({ ...planForm, popular: e.target.checked })} className="w-4 h-4" /> Populær</label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm font-medium"><input type="checkbox" checked={planForm.active} onChange={e => setPlanForm({ ...planForm, active: e.target.checked })} className="w-4 h-4" /> Aktiv i portal</label>
                  </div>
                  <button type="submit" disabled={isSubmitting} className={`w-full text-white py-4 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 transition-colors ${isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}>
                    {isSubmitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSave} />}
                    {isSubmitting ? 'Lagrer...' : 'Lagre Pakke'}
                  </button>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* MODAL: PROMO */}
        <AnimatePresence>
          {showPromoModal && (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-[32px] shadow-2xl w-full max-w-md overflow-hidden">
                <div className="p-6 border-b flex justify-between items-center bg-gray-50">
                  <h3 className="text-xl font-black text-gray-900">{editingPromo ? 'Rediger Kampanje' : 'Ny Kampanje'}</h3>
                  <button onClick={() => setShowPromoModal(false)}><SafeIcon icon={FiX} size={24} /></button>
                </div>
                <form onSubmit={handleSavePromo} className="p-8 space-y-5">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Navn på kampanje</label>
                    <input type="text" placeholder="F.eks. Sommerkampanje 2025" required value={promoForm.name} onChange={e => setPromoForm({ ...promoForm, name: e.target.value })} className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Rabattkode</label>
                    <input type="text" placeholder="Rabattkode" required value={promoForm.code} onChange={e => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })} className="w-full p-3 border border-gray-200 rounded-xl font-mono outline-none focus:border-blue-500" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Type rabatt</label>
                      <select value={promoForm.type} onChange={e => setPromoForm({ ...promoForm, type: e.target.value })} className="w-full p-3 border border-gray-200 rounded-xl bg-white outline-none focus:border-blue-500">
                        <option value="percentage">Prosent (%)</option>
                        <option value="fixed">Fast beløp (kr)</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Verdi</label>
                      <input type="number" placeholder="Verdi" required value={promoForm.value} onChange={e => setPromoForm({ ...promoForm, value: e.target.value })} className="w-full p-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500" />
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">Gyldighetstype</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-gray-700">
                        <input type="radio" name="validity_type" checked={promoForm.validity_type === 'fixed'} onChange={() => setPromoForm({ ...promoForm, validity_type: 'fixed' })} className="w-4 h-4" />
                        Fast dato
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-gray-700">
                        <input type="radio" name="validity_type" checked={promoForm.validity_type === 'relative'} onChange={() => setPromoForm({ ...promoForm, validity_type: 'relative' })} className="w-4 h-4" />
                        Fra bruksdato
                      </label>
                    </div>
                  </div>

                  {promoForm.validity_type === 'fixed' ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Gyldig fra</label>
                        <div className="relative">
                          <SafeIcon icon={FiCalendar} className="absolute left-3 top-3.5 text-gray-400 pointer-events-none" />
                          <input type="date" value={promoForm.valid_from} onChange={e => setPromoForm({ ...promoForm, valid_from: e.target.value })} className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm" />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Gyldig til</label>
                        <div className="relative">
                          <SafeIcon icon={FiCalendar} className="absolute left-3 top-3.5 text-gray-400 pointer-events-none" />
                          <input type="date" value={promoForm.valid_to} onChange={e => setPromoForm({ ...promoForm, valid_to: e.target.value })} className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Varighet (antall)</label>
                        <div className="relative">
                          <SafeIcon icon={FiClock} className="absolute left-3 top-3.5 text-gray-400 pointer-events-none" />
                          <input type="number" min="1" value={promoForm.duration_count} onChange={e => setPromoForm({ ...promoForm, duration_count: e.target.value })} className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500 text-sm" />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Enhet</label>
                        <select value={promoForm.duration_unit} onChange={e => setPromoForm({ ...promoForm, duration_unit: e.target.value })} className="w-full p-3 border border-gray-200 rounded-xl bg-white outline-none focus:border-blue-500 text-sm">
                          <option value="days">Dager</option>
                          <option value="months">Måneder</option>
                          <option value="years">År</option>
                        </select>
                      </div>
                    </div>
                  )}

                  <div className="p-3 bg-gray-50 rounded-2xl border border-gray-100">
                    <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-gray-700">
                      <input type="checkbox" checked={promoForm.active} onChange={e => setPromoForm({ ...promoForm, active: e.target.checked })} className="w-5 h-5 rounded" />
                      Kampanjen er aktiv
                    </label>
                  </div>

                  <button type="submit" disabled={isSubmitting} className={`w-full text-white py-5 rounded-2xl font-black text-lg shadow-xl flex items-center justify-center gap-2 transition-colors ${isSubmitting ? 'bg-orange-400 cursor-not-allowed' : 'bg-[#ea580c] hover:bg-[#c2410c]'}`}>
                    {isSubmitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiCheckCircle} />}
                    {isSubmitting ? 'Lagrer...' : 'Lagre Kampanje'}
                  </button>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* MODAL: EXTERNAL SERVICE */}
        <AnimatePresence>
          {showExternalServiceModal && (
            <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
              <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden">
                <div className="p-6 border-b flex justify-between items-center bg-gray-50">
                  <h3 className="text-xl font-bold">{editingExternalService ? 'Rediger Tjeneste' : 'Ny Tjeneste'}</h3>
                  <button onClick={() => setShowExternalServiceModal(false)}><SafeIcon icon={FiX} size={24} /></button>
                </div>
                <form onSubmit={handleSaveExternalService} className="p-8 space-y-4">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Navn</label>
                    <input type="text" placeholder="F.eks. Varslingspartner" required value={externalServiceForm.name} onChange={e => setExternalServiceForm({ ...externalServiceForm, name: e.target.value })} className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:border-purple-500" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Pris (NOK)</label>
                      <input type="number" placeholder="Pris" required value={externalServiceForm.price} onChange={e => setExternalServiceForm({ ...externalServiceForm, price: e.target.value })} className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:border-purple-500" />
                    </div>
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Fakturering</label>
                      <select value={externalServiceForm.timeframe} onChange={e => setExternalServiceForm({ ...externalServiceForm, timeframe: e.target.value })} className="w-full p-3 border border-gray-300 rounded-xl bg-white outline-none focus:border-purple-500">
                        <option value="mnd">Per måned</option>
                        <option value="år">Per år</option>
                        <option value="sak">Per sak</option>
                        <option value="gang">Engangs</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Beskrivelse</label>
                    <textarea placeholder="Kort beskrivelse av tjenesten som vises i registreringen" rows={3} value={externalServiceForm.description} onChange={e => setExternalServiceForm({ ...externalServiceForm, description: e.target.value })} className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:border-purple-500 resize-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1 block ml-1">Kategori</label>
                      <input type="text" placeholder="external" value={externalServiceForm.category} onChange={e => setExternalServiceForm({ ...externalServiceForm, category: e.target.value })} className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:border-purple-500" />
                    </div>
                    <div className="flex items-end pb-3">
                      <label className="flex items-center gap-2 cursor-pointer text-sm font-medium">
                        <input type="checkbox" checked={externalServiceForm.active} onChange={e => setExternalServiceForm({ ...externalServiceForm, active: e.target.checked })} className="w-4 h-4" /> 
                        Aktiv / Synlig
                      </label>
                    </div>
                  </div>
                  <button type="submit" disabled={isSubmitting} className={`w-full text-white py-4 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 transition-colors mt-4 ${isSubmitting ? 'bg-purple-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'}`}>
                    {isSubmitting ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSave} />}
                    {isSubmitting ? 'Lagrer...' : 'Lagre Tjeneste'}
                  </button>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default PricingManagement;